import PropTypes from 'prop-types';
import { CityPropType, NewBookingDeliveryPropType } from 'common/prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  Fragment,
  useState,
} from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import { Col, Drawer, Form, Popover, Row, Spin } from 'grab-ui';
import infoIcon from 'public/icons/info.svg';
import InputField from 'components/v2/input-field/input-field';
import SelectField from 'components/v2/select-field/select-field';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import {
  enablePaySDKWebSelector,
  enterpriseForPaySdkSelector,
  isCashlessPaymentDisabledSelector,
  isCashPaymentDisabledSelector,
  isPaxSelector,
  selectedCitySelector,
  showEnterpriseTripInputSelector,
} from 'selectors/app-selector';
import { allDeliveriesSelector } from 'selectors/new-booking-selectors';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import {
  ADD_CASHLESS_ON_APP,
  ADD_CASHLESS_ON_GFB_PORTAL,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  FIELD_PLACEHOLDER,
  PAYMENT_TYPE_GRABPAY,
  POSTPAID_BILLING_TYPE,
} from 'constants/booking-constants';
import { getAllPaxPaymentMethodsV2 } from 'actions/booking-actions';
import { showFailedPaymentsRetryModalAction } from 'actions/app-actions';
import { OfferConfigurationField } from 'components/v2/anytime-booking/common';
import paymentIcon from 'components/booking/payment-field/payment-icon';
import formatCurrency from 'common/utils/format-currency';
import calcPrice from 'common/utils/calc-price';
import cx from 'classnames';
import { PAYSDK_PROFILE_TYPES } from 'constants/paysdk-constants';
import { loadPaySdkScript } from 'helper/pay-sdk-helper';
import PaySdkPaymentList from 'components/v2/pay-sdk/pay-sdk-payment-list';
import PaySdkPaymentRecommendation from 'components/v2/pay-sdk/pay-sdk-payment-recommendation';
import { getDeliveryFinalDropOffs } from 'helper/anytime-booking';
import NewBookingDuck from 'ducks/new-booking-duck';
import { deliveryFormFields } from './common';
import styles from './styles.less';

const PaymentDetails = ({
  delivery,
  allDeliveries,
  form,
  paymentMethods,
  isPax,
  isCashPaymentDisabled,
  isCashlessPaymentDisabled,
  city,
  dispatch,
  index,
  showEnterpriseTripInput,
  enablePaySDKWeb,
  enterprise,
}) => {
  const config = _get(delivery, 'config');
  const deliveryDetail = _get(delivery, 'detail');
  const isMSD = useMemo(
    () => {
      const dropOffs = getDeliveryFinalDropOffs(deliveryDetail);
      return _get(dropOffs, 'length', 0) > 1;
    },
    [deliveryDetail],
  );

  const paymentTypeID = _get(
    deliveryDetail,
    deliveryFormFields.paymentTypeID.fieldName,
  );

  const serviceAllowCash = useMemo(
    () => _get(config, 'paymentMethods', ['CASH']).includes('CASH'),
    [config],
  );
  const serviceAllowCard = useMemo(
    () => _get(config, 'paymentMethods', []).includes('CARD'),
    [config],
  );
  const isSameDayService = useMemo(
    () => _get(deliveryDetail, 'configuration.serviceType') === 'SAME_DAY',
    [deliveryDetail],
  );

  const onRetryFailedPayments = useCallback(
    () => {
      dispatch(showFailedPaymentsRetryModalAction());
    },
    [dispatch],
  );

  const onReloadPaymentMethods = useCallback(
    () => {
      dispatch(getAllPaxPaymentMethodsV2(_get(city, 'countryCode')));
    },
    [dispatch, city],
  );

  const makeDisabledPaymentOption = useCallback(
    (option, reason, ctaText, onCTAClick) => (
      <Popover
        content={
          <div>
            <p className={styles.disabledReason}>{reason}</p>
            {ctaText &&
              onCTAClick && (
                <a className={styles.disabledCTA} onClick={onCTAClick}>
                  {ctaText}
                </a>
              )}
          </div>
        }
        trigger={'hover'}
        zIndex={10000}
        placement={'right'}
        overlayClassName={styles.disabledPopover}
      >
        {option}
      </Popover>
    ),
    [],
  );

  const paymentMethodOptions = useMemo(
    () => {
      if (!paymentMethods) return [];

      let anyCashlessPayment = false;
      const options = [];

      paymentMethods.forEach(paymentMethod => {
        const disabled = !paymentMethod.enabled || isCashlessPaymentDisabled;
        switch (paymentMethod.type) {
          case PAYMENT_TYPE_GRABPAY: {
            const isOVO = _get(city, 'countryCode') === 'ID';
            const isMOCA = _get(city, 'countryCode') === 'VN';

            let grabPayIcon;
            if (isOVO) {
              grabPayIcon = paymentIcon.OVO;
            } else if (isMOCA) {
              grabPayIcon = paymentIcon.MOCA;
            } else {
              grabPayIcon = paymentIcon.grabpay;
            }

            const balance = _get(paymentMethod, 'payload.Balance');
            const currency = _get(allDeliveries[0], 'quote.currency');
            if (!disabled) {
              anyCashlessPayment = true;
            }
            const totalAmount = calcPrice(
              allDeliveries.reduce(
                (totalAmount, delivery) =>
                  totalAmount + _get(delivery, 'quote.finalFare.lowerBound', 0),
                0,
              ),
              currency,
            );
            const notEnoughBalance = balance < totalAmount;
            const option = (
              <div
                className={cx(styles.paymentOption, {
                  [styles.disabled]: disabled,
                })}
              >
                <img src={grabPayIcon} alt="" />
                <span>
                  {paymentMethod &&
                    _get(paymentMethod, 'payload.BalanceErrorCode') === 0 &&
                    formatCurrency({
                      code: _get(paymentMethod, 'payload.Currency'),
                      amount: balance,
                    })}
                </span>
              </div>
            );
            options.push({
              value: paymentMethod.paymentTypeID.toString(),
              title: I18n.t(FIELD_PLACEHOLDER.GRAB_PAY),
              child: disabled
                ? makeDisabledPaymentOption(
                    option,
                    I18n.t('v2.failedPayments.bookingPopoverText'),
                    I18n.t('v2.failedPayments.bookingPopoverButton'),
                    onRetryFailedPayments,
                  )
                : notEnoughBalance
                  ? makeDisabledPaymentOption(
                      option,
                      I18n.t(
                        isOVO
                          ? 'ovo.insufficientBalance.popOverText'
                          : 'v2.failedPayments.grabPay.popOverText',
                      ),
                      I18n.t(
                        isOVO
                          ? 'ovo.insufficientBalance.bookingPopoverButton'
                          : 'v2.failedPayments.bookingPopoverButton',
                      ),
                      onReloadPaymentMethods,
                    )
                  : option,
              disabled: disabled || notEnoughBalance,
            });
            return;
          }
          case POSTPAID_BILLING_TYPE: {
            if (!disabled) {
              anyCashlessPayment = true;
            }
            const option = (
              <div
                className={cx(styles.paymentOption, {
                  [styles.disabled]: disabled,
                })}
              >
                <img src={paymentIcon.postpaidBilling} alt="" />
                <span>{I18n.t('booking.paymentMethod.corpBilling')}</span>
              </div>
            );
            options.push({
              value: paymentMethod.paymentTypeID.toString(),
              title: I18n.t(FIELD_PLACEHOLDER.CORP_BILLING),
              child: disabled
                ? makeDisabledPaymentOption(
                    option,
                    I18n.t('v2.failedPayments.bookingPopoverText'),
                    I18n.t('v2.failedPayments.bookingPopoverButton'),
                    onRetryFailedPayments,
                  )
                : option,
              disabled,
            });
            return;
          }
          default: {
            // credit cards
            if (serviceAllowCard) {
              const { type, refNumber } = paymentMethod;
              const accountIcon = _get(paymentMethod, 'payload.AccountIcon');
              if (
                type === POSTPAID_BILLING_TYPE ||
                type === PAYMENT_TYPE_GRABPAY
              ) {
                return;
              }
              if (!disabled) {
                anyCashlessPayment = true;
              }

              const option = (
                <div
                  className={cx(styles.paymentOption, {
                    [styles.disabled]: disabled,
                  })}
                >
                  <img
                    src={
                      accountIcon || paymentIcon[type] || paymentIcon.generic
                    }
                    alt=""
                  />
                  <span>{refNumber}</span>
                </div>
              );
              options.push({
                value: paymentMethod.paymentTypeID.toString(),
                title: refNumber,
                child: disabled
                  ? makeDisabledPaymentOption(
                      option,
                      I18n.t('v2.failedPayments.bookingPopoverText'),
                      I18n.t('v2.failedPayments.bookingPopoverButton'),
                      onRetryFailedPayments,
                    )
                  : option,
                disabled,
              });
            }
          }
        }
      });

      // cash
      if (serviceAllowCash && (isSameDayService || !isCashPaymentDisabled)) {
        options.push(
          {
            value: CASH_BY_SENDER,
            title: I18n.t('booking.paymentMethod.cashBySender'),
            child: (
              <div className={styles.paymentOption}>
                <img src={paymentIcon.CASH} alt="" />
                <span>{I18n.t('booking.paymentMethod.cashBySender')}</span>
              </div>
            ),
          },
          {
            value: CASH_BY_RECIPIENT,
            title: I18n.t('booking.paymentMethod.cashByRecipient'),
            child: (
              <div className={styles.paymentOption}>
                <img src={paymentIcon.CASH} alt="" />
                <span>{I18n.t('booking.paymentMethod.cashByRecipient')}</span>
              </div>
            ),
            disabled: isMSD,
          },
        );
      }

      if (!anyCashlessPayment) {
        if (isPax) {
          options.push({
            value: ADD_CASHLESS_ON_APP,
            title: I18n.t(
              'phrase.common.express_booking_flow_helpertext_use_grabapp_single_stop',
            ),
            child: (
              <div className={styles.paymentOption}>
                <img src={infoIcon} alt="" />
                <span>
                  {I18n.t(
                    'phrase.common.express_booking_flow_helpertext_use_grabapp_single_stop',
                  )}
                </span>
              </div>
            ),
            disabled: true,
          });
        } else {
          options.push({
            value: ADD_CASHLESS_ON_GFB_PORTAL,
            title: I18n.t(
              'phrase.common.express_booking_flow_helpertext_use_grabforbusiness_single_stop',
            ),
            child: (
              <div className={styles.paymentOption}>
                <img src={infoIcon} alt="" />
                <span>
                  {I18n.t(
                    'phrase.common.express_booking_flow_helpertext_use_grabforbusiness_single_stop',
                  )}
                </span>
              </div>
            ),
            disabled: true,
          });
        }
      }

      return _sortBy(options, p => p.disabled);
    },
    [
      city,
      isMSD,
      allDeliveries,
      paymentMethods,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      serviceAllowCash,
      serviceAllowCard,
      isSameDayService,
      isPax,
      makeDisabledPaymentOption,
      onReloadPaymentMethods,
      onRetryFailedPayments,
    ],
  );

  // Declared here so the useEffect below can reference it in its dependency array
  // without hitting a temporal dead zone (const declarations are not hoisted).
  const showPaySdk =
    enablePaySDKWeb && serviceAllowCard && !isCashlessPaymentDisabled;

  useEffect(
    () => {
      // PaySDK manages selection; paymentMethodOptions doesn't include wallet methods.
      if (showPaySdk) return;

      const validPaymentMethodOptions = paymentMethodOptions.filter(
        pmo => !pmo.disabled,
      );
      const firstValidPaymentMethodOption = validPaymentMethodOptions[0];

      // select first payment method
      if (config && !paymentTypeID && firstValidPaymentMethodOption) {
        form.setFieldsValue({
          [deliveryFormFields.paymentTypeID.fieldName]: _get(
            firstValidPaymentMethodOption,
            'value',
          ),
        });
      }

      // remove invalid selection
      if (paymentTypeID) {
        const selectedValid = validPaymentMethodOptions.find(
          pmo => pmo.value === paymentTypeID,
        );
        if (!selectedValid) {
          form.setFieldsValue({
            [deliveryFormFields.paymentTypeID.fieldName]: _get(
              firstValidPaymentMethodOption,
              'value',
            ),
          });
        }
      }
    },
    [showPaySdk, form, paymentTypeID, paymentMethods, paymentMethodOptions],
  );

  // One stable UUID per delivery payment surface — scopes PaySDK browser state so that
  // recommendation/list widgets for this delivery are isolated from other deliveries on the page.
  // See integration-guide.md#multiple-paysdk-widgets-on-one-page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paySdkStateScope = useMemo(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID)
      return crypto.randomUUID();
    return `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }, []);

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [paySdkScriptReady, setPaySdkScriptReady] = useState(false);
  // True after an explicit user selection; blocks recommendation auto-fires.
  // Reset when delivery changes so a fresh delivery gets a fresh recommendation.
  const hasManuallySelectedRef = useRef(false);
  useEffect(
    () => {
      hasManuallySelectedRef.current = false;
    },
    [delivery.id],
  );
  const isCashPaymentType =
    paymentTypeID === CASH_BY_SENDER || paymentTypeID === CASH_BY_RECIPIENT;
  const [paySdkSelectedCash, setPaySdkSelectedCash] = useState(
    () => isCashPaymentType,
  );
  // Last PaySDK ID — passed to PaySdkPaymentList to pre-highlight the active method.
  const [paySdkRawPaymentID, setPaySdkRawPaymentID] = useState('');

  useEffect(
    () => {
      setPaySdkSelectedCash(isCashPaymentType);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentTypeID],
  );

  const showCashPaidByDropdown =
    showPaySdk &&
    paySdkSelectedCash &&
    serviceAllowCash &&
    (isSameDayService || !isCashPaymentDisabled);

  const location = useMemo(
    () => {
      const pickUpPoi = _get(allDeliveries, '[0].detail.pickUp.poi', {});
      const latlng = _get(pickUpPoi, 'latlng', {});
      return {
        latitude: _get(latlng, 'latitude', 0),
        longitude: _get(latlng, 'longitude', 0),
      };
    },
    [allDeliveries],
  );

  // Dispatches PaySDK selection (id, type, name) directly to Redux.
  // Does not call form.setFieldsValue to avoid triggering onFieldsChange.
  const applyPaySdkSelection = useCallback(
    ({ paymentTypeId, paymentMethodType, shortTitle }) => {
      const paymentIDStr = String(paymentTypeId);
      setPaySdkRawPaymentID(paymentIDStr);
      const isCashSelection = paymentIDStr === '0';

      if (isCashSelection) {
        // Only apply if cash is actually allowed for this service/context.
        if (!serviceAllowCash || (!isSameDayService && isCashPaymentDisabled)) {
          return;
        }
        setPaySdkSelectedCash(true);
        dispatch(
          NewBookingDuck.creators.updateDeliveryDetail(
            delivery.id,
            deliveryFormFields.paymentTypeID.fieldName,
            CASH_BY_SENDER,
          ),
        );
        // Clear stale type/name/id so a previous card selection doesn't bleed into the payload.
        dispatch(
          NewBookingDuck.creators.updateDeliveryDetail(
            delivery.id,
            'paySdkPaymentID',
            '',
          ),
        );
        dispatch(
          NewBookingDuck.creators.updateDeliveryDetail(
            delivery.id,
            'paySdkPaymentType',
            '',
          ),
        );
        dispatch(
          NewBookingDuck.creators.updateDeliveryDetail(
            delivery.id,
            'paySdkPaymentName',
            '',
          ),
        );
        return;
      }

      setPaySdkSelectedCash(false);
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          deliveryFormFields.paymentTypeID.fieldName,
          paymentIDStr,
        ),
      );
      // paySdkPaymentID is the authoritative PaySDK card ID — set directly from PaySDK events,
      // isolated from form state so it cannot be corrupted by antd field changes.
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          'paySdkPaymentID',
          paymentIDStr,
        ),
      );
      // Always dispatch even if empty to clear stale values from a previous selection.
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          'paySdkPaymentType',
          paymentMethodType,
        ),
      );
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          'paySdkPaymentName',
          shortTitle,
        ),
      );
    },
    [
      dispatch,
      delivery,
      serviceAllowCash,
      isSameDayService,
      isCashPaymentDisabled,
    ],
  );

  // Ignore events when the drawer is closed — those are PaySDK auto-fires
  // (resize, re-init) and should not override the user's explicit choice.
  const handlePaySdkPaymentSelect = useCallback(
    selection => {
      if (!isPaymentDrawerOpen) return;
      hasManuallySelectedRef.current = true;
      applyPaySdkSelection(selection);
    },
    [applyPaySdkSelection, isPaymentDrawerOpen],
  );

  const handleOpenPaymentList = useCallback(
    () => setIsPaymentDrawerOpen(true),
    [],
  );

  const handlePaymentRecommended = useCallback(
    selection => {
      if (!hasManuallySelectedRef.current) {
        applyPaySdkSelection(selection);
      }
    },
    [applyPaySdkSelection],
  );

  // Clear paySdkPaymentID when PaySDK is not shown so that a stale card ID from a
  // previous PaySDK session cannot bleed into the booking payload via effectivePaymentID.
  useEffect(
    () => {
      if (showPaySdk) return;
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          'paySdkPaymentID',
          '',
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showPaySdk],
  );

  useEffect(
    () => {
      if (!showPaySdk) return;
      loadPaySdkScript()
        .then(() => setPaySdkScriptReady(true))
        .catch(() => setPaySdkScriptReady(false));
    },
    [showPaySdk],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.homepage.manualOrder.heading.paymentDetails')}
      </div>

      <Row type="flex" align="top" gutter={16}>
        {showPaySdk ? (
          <Col xs={24} md={8}>
            <Form.Item className={styles.inputField}>
              {!showCashPaidByDropdown &&
                form.getFieldDecorator(
                  deliveryFormFields.paymentTypeID.fieldName,
                  {
                    initialValue: _get(
                      deliveryDetail,
                      deliveryFormFields.paymentTypeID.fieldName,
                    ),
                    rules: deliveryFormFields.paymentTypeID.makeRules(),
                  },
                )(<input type="hidden" />)}
              <PaySdkPaymentRecommendation
                onOpenPaymentList={handleOpenPaymentList}
                onPaymentRecommended={handlePaymentRecommended}
                selectedPaymentTypeID={paymentTypeID}
                deliveries={[delivery]}
                location={location}
                countryCode={_get(city, 'countryCode')}
                enterprise={enterprise}
                stateScope={paySdkStateScope}
              />
            </Form.Item>
          </Col>
        ) : (
          <Col xs={24} md={8}>
            <SelectField
              form={form}
              id={deliveryFormFields.paymentTypeID.fieldName}
              fieldOptions={{
                initialValue: _get(
                  deliveryDetail,
                  deliveryFormFields.paymentTypeID.fieldName,
                ),
                rules: deliveryFormFields.paymentTypeID.makeRules(),
              }}
              placeholder={I18n.t(
                'v2.homepage.manualOrder.placeholder.paymentMethod',
              )}
              options={paymentMethodOptions}
            />
          </Col>
        )}
        {showCashPaidByDropdown && (
          <Col xs={24} md={8}>
            <SelectField
              form={form}
              id={deliveryFormFields.paymentTypeID.fieldName}
              fieldOptions={{
                initialValue: CASH_BY_SENDER,
                rules: deliveryFormFields.paymentTypeID.makeRules(),
              }}
              placeholder={I18n.t(
                'v2.homepage.manualOrder.placeholder.paymentMethod',
              )}
              fieldClassName={styles.inputField}
              optionClassName={styles.itemCategoryOption}
              options={[
                {
                  value: CASH_BY_SENDER,
                  title: I18n.t('booking.paymentMethod.cashBySender'),
                  child: I18n.t('booking.paymentMethod.cashBySender'),
                },
                {
                  value: CASH_BY_RECIPIENT,
                  title: I18n.t('booking.paymentMethod.cashByRecipient'),
                  child: I18n.t('booking.paymentMethod.cashByRecipient'),
                  disabled: isMSD,
                },
              ]}
            />
          </Col>
        )}
        <Col xs={24} md={8}>
          <InputField
            form={form}
            id={deliveryFormFields.referenceCode.fieldName}
            fieldOptions={{
              initialValue: _get(
                deliveryDetail,
                deliveryFormFields.referenceCode.fieldName,
              ),
              rules: deliveryFormFields.referenceCode.makeRules(),
            }}
            placeholder={I18n.t(
              'v2.homepage.manualOrder.placeholder.referenceCode',
            )}
            fieldClassName={styles.inputField}
          />
        </Col>
        <Col xs={24} md={8}>
          <OfferConfigurationField delivery={delivery} index={index} />
        </Col>
        {showEnterpriseTripInput && (
          <Fragment>
            <Col xs={24} md={8} className={styles.tripInputField}>
              <InputField
                form={form}
                id={deliveryFormFields.tripCode.fieldName}
                fieldOptions={{
                  initialValue: _get(
                    deliveryDetail,
                    deliveryFormFields.tripCode.fieldName,
                  ),
                  rules: deliveryFormFields.tripCode.makeRules(),
                }}
                placeholder={I18n.t(
                  'v2.homepage.manualOrder.placeholder.tripCode',
                )}
                fieldClassName={styles.inputField}
              />
            </Col>
            <Col xs={24} md={8} className={styles.tripInputField}>
              <InputField
                form={form}
                id={deliveryFormFields.tripDescription.fieldName}
                fieldOptions={{
                  initialValue: _get(
                    deliveryDetail,
                    deliveryFormFields.tripDescription.fieldName,
                  ),
                  rules: deliveryFormFields.tripDescription.makeRules(),
                }}
                placeholder={I18n.t(
                  'v2.homepage.manualOrder.placeholder.tripDescription',
                )}
                fieldClassName={styles.inputField}
              />
            </Col>
          </Fragment>
        )}
      </Row>
      {showPaySdk && (
        <Drawer
          width={495}
          placement="right"
          visible={isPaymentDrawerOpen}
          onClose={() => setIsPaymentDrawerOpen(false)}
          closable
          height="100%"
          className={styles.paymentDrawer}
        >
          <div className={styles.paymentDrawerBody}>
            {paySdkScriptReady ? (
              <PaySdkPaymentList
                onSelectPayment={handlePaySdkPaymentSelect}
                selectedPaymentTypeID={paySdkRawPaymentID}
                deliveries={[delivery]}
                location={location}
                countryCode={_get(city, 'countryCode')}
                enterprise={enterprise}
                stateScope={paySdkStateScope}
              />
            ) : (
              <div className={styles.paymentDrawerLoading}>
                <Spin
                  tip={I18n.t('v2.homepage.manualOrder.loadingPaymentOptions')}
                />
              </div>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
};

PaymentDetails.defaultProps = {
  city: undefined,
  isCashPaymentDisabled: false,
  isCashlessPaymentDisabled: false,
  showEnterpriseTripInput: false,
  enablePaySDKWeb: false,
  enterprise: { profileType: PAYSDK_PROFILE_TYPES.PERSONAL },
};

PaymentDetails.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  allDeliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  city: CityPropType,
  form: PropTypes.shape({}).isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isPax: PropTypes.bool.isRequired,
  isCashPaymentDisabled: PropTypes.bool,
  isCashlessPaymentDisabled: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  showEnterpriseTripInput: PropTypes.bool,
  enablePaySDKWeb: PropTypes.bool,
  enterprise: PropTypes.shape({
    groupID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    profileType: PropTypes.string,
  }),
};

export default connect(state => ({
  paymentMethods: paymentMethodSelector(state),
  isPax: isPaxSelector(state),
  city: selectedCitySelector(state),
  allDeliveries: allDeliveriesSelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
  showEnterpriseTripInput: showEnterpriseTripInputSelector(state),
  enablePaySDKWeb: enablePaySDKWebSelector(state),
  enterprise: enterpriseForPaySdkSelector(state),
}))(PaymentDetails);
